import React from 'react';
import { Truck, X, Upload, Clock, Package, CheckCircle, Search, ArrowBigLeft, Download } from 'lucide-react';
import '../../../../../styles/components/vehicle-register.scss';
import { toast } from 'react-toastify';
import i18n from '../../../../../i18n';
import { createWeighbridgeTicket, exitWeighbridgeTicket, getDeliverySitesList, getVehiclesForSite, getWeighbridgeTickets } from '../services/MaterialLoadingService';

class MaterialLoading extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      type: "non-opal",
      yardId: '',
      date: "",
      auctionSite: '', // This is the selected site ID
      vehicleId: '', //
      sites: [],
      vehicles: [],
      loadingSites: false,
      loadingVehicles: false,
      hideSidebar: false,
      errors: {},
      isEditing: false, // Track if we're editing an existing ticket
      currentTime: new Date().toISOString(),

      // Ticket Basic Information
      ticketId: 'TKT-' + Date.now().toString().slice(-6),
      operationType: 'check-in', // 'check-in', 'check-out'
      ticketDate: new Date().toISOString().split('T')[0],
      ticketTime: new Date().toTimeString().slice(0, 5),
      ticketStatus: 'loading', // 'in-progress', 'completed', 'cancelled'
      checklist: {
        safetyChecked: false,
        VehicleChecked: false,

      },
      // Vehicle Information
      vehicleNumber: '',
      vehicleType: '',
      vehicleModel: '',
      vehicleCapacity: '',

      // Driver Information
      driverName: '',
      driverContact: '',
      driverLicense: '',

      tareWeight: '',
      grossWeight: '',
      netWeight: '',
      weighbridgeLocation: '',

      // Location Information
      loadingPoint: 'NIMR',
      destination: 'MUSCAT',
      yardLocation: '',
      facility: '',

      // Material Information
      materialType: '',
      auctionLot: '',
      materialQuantity: '',

      // Documentation
      waybillNumber: '',
      gatePassNumber: '',
      bosNumber: '',
      supervisorName: '',

      // Safety & Compliance
      safetyStatus: 'pending',
      technicalClearance: false,
      financialClearance: false,
      inspectionNotes: '',

      // Timing Information (auto-filled based on operation)
      actualArrival: '',
      actualDeparture: '',
      processingDuration: '',
      vehiclePhoto: '',
      // Photos
      vehiclePhotos: [],

      recentTickets: [],
      searchQuery: ''
    };
  }

  // Calculate processing duration
  // Add interval to update current time
  componentDidMount() {
    this.timeInterval = setInterval(() => {
      this.setState({ currentTime: new Date().toISOString() });
    }, 60000); // Update every minute

    const today = new Date().toISOString().split("T")[0];
    this.setState({ date: today });
    this.fetchSitesList();
    this.fetchWeighbridgeTickets(); // Fetch real tickets from API
  }


  componentWillUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  // Updated duration calculation for real-time updates
  // Human-readable duration format
  calculateProcessingDuration = () => {
    const { actualArrival, actualDeparture, currentTime } = this.state;

    const endTime = actualDeparture || currentTime;

    if (!actualArrival || !endTime) {
      return 'Not available';
    }

    try {
      const arrival = new Date(actualArrival);
      const departure = new Date(endTime);

      if (isNaN(arrival.getTime()) || isNaN(departure.getTime())) {
        return 'Invalid time format';
      }

      const durationMs = departure - arrival;

      if (durationMs < 0) {
        return 'Invalid time range';
      }

      const durationMinutes = Math.round(durationMs / (1000 * 60));
      const days = Math.floor(durationMinutes / (60 * 24));
      const hours = Math.floor((durationMinutes % (60 * 24)) / 60);
      const minutes = durationMinutes % 60;

      let result = '';

      if (days > 0) {
        result = `${days} day${days > 1 ? 's' : ''}`;
        if (hours > 0) {
          result += `, ${hours} hour${hours > 1 ? 's' : ''}`;
        }
        if (minutes > 0 && days < 3) { // Only show minutes for shorter durations
          result += `, ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
      } else if (hours > 0) {
        result = `${hours} hour${hours > 1 ? 's' : ''}`;
        if (minutes > 0) {
          result += `, ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
      } else {
        result = `${minutes} minute${minutes > 1 ? 's' : ''}`;
      }

      // Add indicator if still processing
      if (!actualDeparture) {
        result += ' (ongoing)';
      }

      return result;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return 'Calculation error';
    }
  };
  handleChecklistChange = (item) => {
    this.setState(prevState => ({
      checklist: {
        ...prevState.checklist,
        [item]: !prevState.checklist[item]
      }
    }));
  };

  // Handle clicking on a recent ticket to populate the form
  handleTicketClick = (ticket) => {
    // Convert status from API to your frontend status
    let ticketStatus = ticket.status;
    const yards = ticket.delivery_site?.yards || [];

    if (ticket.status === 'loading' && ticket.exit_time) {
      ticketStatus = 'completed';
    }

    this.setState({
      isEditing: true,

      // Basic Information
      ticketId: ticket.ticketNumber,
      operationType: ticket.status === 'IN' ? 'check-in' : 'check-out',
      ticketDate: new Date().toISOString().split('T')[0], // Reset to current date for editing
      ticketTime: new Date().toTimeString().slice(0, 5), // Reset to current time
      ticketStatus: ticketStatus,

      yardId: '',

      currentTicketYards: yards,

      // Vehicle Information
      vehicleNumber: ticket.vehicleNumber,
      vehicleId: ticket.vehicle?.id || '', // Store vehicle ID for editing

      // Driver Information
      driverName: ticket.driverName,
      driverContact: ticket.driverContact || '',
      driverLicense: ticket.driverLicense || '',

      tareWeight: ticket.tareWeight || '',
      grossWeight: ticket.grossWeight || '',
      netWeight: ticket.netWeight || '',
      weighbridgeLocation: ticket.weighbridgeLocation || '',

      // Location Information
      loadingPoint: ticket.loadingPoint || '',
      destination: ticket.destination || '',
      yardLocation: ticket.yardLocation || '',
      facility: ticket.facility || '',
      vehiclePhoto: ticket.vehiclePhoto,

      // Material Information
      materialType: ticket.materialType || '',
      auctionLot: ticket.auctionLot || '',
      materialQuantity: ticket.materialQuantity || '',

      // Documentation
      waybillNumber: ticket.waybillNumber || '',
      gatePassNumber: ticket.gatePassNumber || '',
      bosNumber: ticket.bosNumber || '',
      supervisorName: ticket.supervisorName || '',

      // Safety & Compliance
      safetyStatus: ticket.safetyStatus || 'pending',
      technicalClearance: ticket.technicalClearance || false,
      financialClearance: ticket.financialClearance || false,
      inspectionNotes: ticket.inspectionNotes || '',

      // Timing Information
      actualArrival: ticket.checkInTime || '',
      actualDeparture: ticket.checkOutTime || '',
      processingDuration: ticket.processingDuration || '',

      errors: {} // Clear any existing errors
    });

    // Show success message
    toast.success(`Loaded ticket ${ticket.ticketNumber} for editing`);
  };

  // Handle input changes
  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      errors: { ...this.state.errors, [field]: '' } // Clear error when user types
    });

    // Auto-calculate net weight when tare or gross weight changes
    if (field === 'grossWeight') {
      this.calculateNetWeight();
    }

    // Auto-fill timing information based on operation type
    if (field === 'operationType') {
      this.handleOperationTypeChange(value);
    }
  };

  // Calculate net weight automatically
  calculateNetWeight = () => {
    const { tareWeight, grossWeight } = this.state;
    if (tareWeight && grossWeight) {
      const net = parseFloat(grossWeight) - parseFloat(tareWeight);
      this.setState({ netWeight: net > 0 ? net.toString() : '' });
    } else {
      this.setState({ netWeight: '' });
    }
  };

  // Handle operation type change
  handleOperationTypeChange = (operationType) => {
    const currentTime = new Date().toISOString().slice(0, 19).replace('T', ' ');

    if (operationType === 'check-in') {
      this.setState({
        actualArrival: currentTime,
        actualDeparture: '',
        processingDuration: ''
      });
    } else if (operationType === 'check-out') {
      this.setState({
        actualDeparture: currentTime,
        processingDuration: this.calculateProcessingDuration()
      });
    }
  };

  // Calculate processing duration
  // calculateProcessingDuration = () => {
  //   const { actualArrival, actualDeparture } = this.state;
  //   if (actualArrival && actualDeparture) {
  //     const arrival = new Date(actualArrival);
  //     const departure = new Date(actualDeparture);
  //     const duration = Math.round((departure - arrival) / (1000 * 60)); // minutes
  //     return `${duration} minutes`;
  //   }
  //   return '';
  // };

  // Form validation
  validateForm = () => {
    const errors = {};

    // Required by backend
    if (!this.state.vehicleId) errors.vehicleId = 'Vehicle selection is required';
    if (!this.state.auctionSite) errors.auctionSite = 'Delivery site is required';
    if (!this.state.tareWeight || parseFloat(this.state.tareWeight) <= 0) {
      errors.tareWeight = 'Valid tare weight is required';
    }

    // Optional validation
    if (this.state.operationType === 'check-out') {
      if (!this.state.grossWeight || parseFloat(this.state.grossWeight) <= 0) {
        errors.grossWeight = 'Valid gross weight is required for check-out';
      }
    }

    return errors;
  };

  handleTypeChange = (value) => {
    this.setState({
      type: value,
    });
  }
  // Handle form submission
  handleSubmit = async () => {
    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    try {
      this.setState({ isLoading: true });

      // Prepare data for API based on your backend requirements
      const ticketData = {
        vehicle_id: this.state.vehicleId, // This should be the vehicle ID, not number
        delivery_site_id: this.state.auctionSite, // Using auctionSite as delivery_site_id
        driver_id: null, // Optional - you can get driver ID if you have it
        tare_weight: this.state.tareWeight, // Required at entry
      };

      // If you have driver ID from vehicle data, add it
      if (this.state.vehicleId) {
        const selectedVehicle = this.state.vehicles.find(v => v.id == this.state.vehicleId);
        if (selectedVehicle?.driver?.id) {
          ticketData.driver_id = selectedVehicle.driver.id;
        }
      }

      console.log('Creating weighbridge ticket with:', ticketData);

      // Call the API
      const response = await createWeighbridgeTicket(ticketData);

      if (response.success) {
        toast.success(`Ticket ${response.data.ticket_number} created successfully!`);

        // Show success with ticket details
        this.setState({
          showSuccessModal: true,
          createdTicket: response.data,
          ticketNumber: response.data.ticket_number,
          createdTicketId: response.data.id,
          ticketId: response.data.ticket_number // Update the displayed ticket ID
        });

        // Reset form or keep data as needed
        // this.resetForm(); // You might want to keep some data

        // Show the success modal with ticket details
        setTimeout(() => {
          this.setState({ showSuccessModal: false });
        }, 5000);

      } else {
        // Show backend validation errors
        if (response.errors) {
          const apiErrors = {};
          Object.keys(response.errors).forEach(key => {
            apiErrors[key] = response.errors[key][0]; // Get first error message
          });
          this.setState({ errors: apiErrors });
          toast.error('Please check the form for errors');
        } else {
          toast.error(response.message || 'Failed to create ticket');
        }
      }

    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket: ' + (error.message || 'Unknown error'));
    } finally {
      this.setState({ isLoading: false });
    }
  };


  handleExitSubmit = async () => {
    // Validation specific to exit
    const errors = {};

    if (!this.state.ticketId || this.state.ticketId.startsWith('TKT-')) {
      errors.ticketId = 'Please select a valid existing ticket to check-out';
    }

    if (!this.state.grossWeight || parseFloat(this.state.grossWeight) <= 0) {
      errors.grossWeight = 'Valid gross weight is required for check-out';
    }

    if (this.state.vehiclePhotos.length === 0) {
      errors.photos = 'At least one photo is required for check-out';
    }

    // Check if the delivery site has yards
    const selectedTicket = this.state.recentTickets.find(t => t.ticketNumber === this.state.ticketId);
    if (selectedTicket && selectedTicket.delivery_site && selectedTicket.delivery_site.yards && selectedTicket.delivery_site.yards.length > 0) {
      // If delivery site has yards, require yard selection
      if (!this.state.yardId) {
        errors.yardId = 'Please select a yard for this delivery site';
      }
    }

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    try {
      this.setState({ isLoading: true });

      // Prepare FormData for exit
      const formData = new FormData();
      formData.append('gross_weight', this.state.grossWeight);

      // Add yard_id if selected
      if (this.state.yardId) {
        formData.append('yard_id', this.state.yardId);
      }

      // Get the ticket ID from the ticket number
      const ticketId = this.getTicketIdFromNumber(this.state.ticketId);

      if (!ticketId) {
        toast.error('Ticket ID not found');
        this.setState({ isLoading: false });
        return;
      }

      // Add photos
      if (this.state.vehiclePhotos.length > 0) {
        this.state.vehiclePhotos.forEach((photo, index) => {
          if (photo.file) {
            formData.append('photos[]', photo.file, photo.file.name || `vehicle_photo_${index}.jpg`);
          }
        });
      }

      console.log('Exit FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0], ':', pair[1]);
      }

      console.log('Recording exit for ticket:', ticketId, 'with gross weight:', this.state.grossWeight, 'yard_id:', this.state.yardId);

      // Call the API
      const response = await exitWeighbridgeTicket(formData, true, ticketId);

      if (response.success) {
        toast.success(`Check-out recorded successfully for ticket ${this.state.ticketId}!`);

        // Reset yard selection
        this.setState({
          yardId: '',
          showSuccessModal: true,
          updatedTicket: response.data,
          ticketNumber: response.data.ticket_number,
          operationType: 'check-in',
          ticketId: 'TKT-' + Date.now().toString().slice(-6),
          grossWeight: '',
          netWeight: '',
          vehiclePhotos: [],
          errors: {}
        });

        this.fetchWeighbridgeTickets();

        setTimeout(() => {
          this.setState({ showSuccessModal: false });
        }, 5000);

      } else {
        if (response.errors) {
          const apiErrors = {};
          Object.keys(response.errors).forEach(key => {
            apiErrors[key] = response.errors[key][0];
          });
          this.setState({ errors: apiErrors });
          toast.error('Please check the form for errors');
        } else {
          toast.error(response.message || 'Failed to record exit');
        }
      }

    } catch (error) {
      console.error('Error recording exit:', error);
      toast.error('Failed to record exit: ' + (error.message || 'Unknown error'));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  // Helper function to extract ticket ID from ticket number
  // Helper function to extract ticket ID from ticket number
  getTicketIdFromNumber = (ticketNumber) => {
    const ticket = this.state.recentTickets.find(t => t.ticketNumber === ticketNumber);
    if (!ticket) {
      console.error('Ticket not found in recentTickets:', ticketNumber);
      console.log('Available tickets:', this.state.recentTickets.map(t => t.ticketNumber));
    }
    return ticket ? ticket.id : null;
  };

  // Add function to handle photo upload
  handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 0) {
      const uploadedPhotos = files.map(file => {
        // Validate file type and size for each file
        if (!file.type.startsWith('image/')) {
          toast.error('Please upload image files only');
          return null;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          toast.error(`File ${file.name} size should be less than 5MB`);
          return null;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);

        return {
          id: Date.now() + Math.random(),
          url: previewUrl,
          file: file,
          name: file.name
        };
      }).filter(photo => photo !== null); // Remove null entries (invalid files)

      if (uploadedPhotos.length > 0) {
        this.setState(prevState => ({
          vehiclePhotos: [...prevState.vehiclePhotos, ...uploadedPhotos],
          errors: { ...prevState.errors, photos: '' }
        }));

        toast.success(`${uploadedPhotos.length} photo(s) uploaded successfully`);
      }
    }
  };

  // Update photo removal to handle file cleanup
  removePhoto = (id) => {
    this.setState((prevState) => {
      const photoToRemove = prevState.vehiclePhotos.find(p => p.id === id);
      if (photoToRemove && photoToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(photoToRemove.url);
      }

      return {
        vehiclePhotos: prevState.vehiclePhotos.filter((photo) => photo.id !== id),
        vehiclePhotoFile: null,
        errors: { ...prevState.errors, photos: '' } // Changed from vehiclePhotos to photos
      };
    });
  };

  // Reset form for new ticket
  resetForm = () => {
    this.setState({
      isEditing: false,
      ticketId: 'TKT-' + Date.now().toString().slice(-6),
      operationType: 'check-in',
      ticketDate: new Date().toISOString().split('T')[0],
      ticketTime: new Date().toTimeString().slice(0, 5),
      ticketStatus: 'loading',
      vehicleNumber: '',
      trailerNumber: '',
      vehicleType: '',
      vehicleModel: '',
      vehicleCapacity: '',
      driverName: '',
      driverContact: '',
      driverLicense: '',
      tareWeight: '',
      grossWeight: '',
      netWeight: '',
      weighbridgeLocation: '',
      loadingPoint: '',
      destination: '',
      yardLocation: '',
      facility: '',
      materialType: '',
      auctionLot: '',
      materialQuantity: '',
      waybillNumber: '',
      gatePassNumber: '',
      bosNumber: '',
      supervisorName: '',
      safetyStatus: 'pending',
      technicalClearance: false,
      financialClearance: false,
      inspectionNotes: '',
      actualArrival: '',
      actualDeparture: '',
      processingDuration: '',
      errors: {}
    });
  };

  // Handle safety check approval
  handleSafetyCheck = () => {
    this.setState({
      safetyStatus: 'approved',
      errors: { ...this.state.errors, safetyStatus: '' }
    });
    toast.success('Safety check approved!');
  };



  // Search recent tickets
  handleSearch = (query) => {
    this.setState({ searchQuery: query });
  };



  // Get filtered recent tickets
  getFilteredTickets = () => {
    const { recentTickets, searchQuery } = this.state;
    if (!searchQuery) return recentTickets.slice(0, 10); // Show only 4 by default

    return recentTickets.filter(ticket =>
      ticket.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.driverName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  fetchSitesList = async () => {
    this.setState({ loadingSites: true });

    try {
      const filters = {
      };

      const response = await getDeliverySitesList(filters);

      if (response.success && response.data) {
        // Transform the data to match your dropdown needs
        const sites = response.data.map(site => ({
          id: site.value,
          value: site.value,
          label: site.label,
          name: site.label,
          load_from: site.load_from || '', // Make sure to include load_from
          load_to: site.load_to || '',     // Make sure to include load_to
          data: site.data || {}
        }));

        console.log('Processed Sites:', sites); // Debug log

        this.setState({
          sites,
          loadingSites: false
        });
      } else {
        toast.error(response.message || 'Failed to fetch sites');
        this.setState({ loadingSites: false });
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Error loading sites list');
      this.setState({ loadingSites: false });
    }
  };

  fetchVehiclesList = async (siteId) => {
    this.setState({ loadingVehicles: true });

    try {
      const filters = {};
      const response = await getVehiclesForSite(siteId, filters);

      console.log('Vehicles API Response:', response); // Debug log

      if (response.success && response.data) {
        // Transform the data based on your API response structure
        let vehicles = [];

        if (Array.isArray(response.data)) {
          vehicles = response.data.map(item => {
            // Check different possible response structures
            const vehicle = item.vehicle || item; // Try vehicle property first, then item itself

            // Check if driver info exists in the response
            const driver = vehicle.driver || item.driver || null;

            return {
              id: vehicle.id,
              value: vehicle.id,
              label: vehicle.vehicle_number || `Vehicle ${vehicle.id}`,
              name: vehicle.vehicle_number,
              vehicle_number: vehicle.vehicle_number,
              vehicle_model: vehicle.vehicle_model,
              capacity: vehicle.capacity,
              status: vehicle.status,
              driver: driver ? {
                id: driver.id,
                name: driver.name || driver.driver_name,
                phone: driver.phone || driver.mobile || driver.driver_contact,
                license_number: driver.license_number || driver.driver_license
              } : null
            };
          });
        } else if (response.data.vehicles) {
          // If response has a vehicles array
          vehicles = response.data.vehicles.map(vehicle => {
            const driver = vehicle.driver || null;

            return {
              id: vehicle.id,
              value: vehicle.id,
              label: vehicle.vehicle_number || `Vehicle ${vehicle.id}`,
              name: vehicle.vehicle_number,
              vehicle_number: vehicle.vehicle_number,
              vehicle_model: vehicle.vehicle_model,
              capacity: vehicle.capacity,
              status: vehicle.status,
              driver: driver ? {
                id: driver.id,
                name: driver.name,
                phone: driver.phone || driver.mobile,
                license_number: driver.license_number
              } : null
            };
          });
        }

        console.log('Processed Vehicles:', vehicles); // Debug log

        this.setState({
          vehicles,
          loadingVehicles: false
        });
      } else {
        toast.error(response.message || 'Failed to fetch vehicles for this site');
        this.setState({ loadingVehicles: false, vehicles: [] });
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error loading vehicles list');
      this.setState({
        loadingVehicles: false,
        vehicles: [],
        vehicleId: '',
        vehicleNumber: '',
        driverName: '',
        driverContact: '',
        driverLicense: ''
      });
    }
  };

  fetchWeighbridgeTickets = async () => {
    this.setState({ isLoadingTickets: true });

    try {
      const filters = {};
      const response = await getWeighbridgeTickets(filters);

      console.log('Weighbridge Tickets API Response:', response); // Debug log

      if (response.success && response.data && response.data.data) {
        // Transform the API response to match your recentTickets format
        const tickets = response.data.data.map(ticket => {
          // Convert status from 'IN'/'OUT' to your frontend statuses
          let status = 'loading';
          if (ticket.status === 'IN') {
            status = 'loading';
          } else if (ticket.status === 'OUT') {
            status = 'completed';
          }

          // Calculate duration if available
          let processingDuration = '';
          if (ticket.entry_time && ticket.exit_time) {
            const entry = new Date(ticket.entry_time);
            const exit = new Date(ticket.exit_time);
            const durationMs = exit - entry;
            const minutes = Math.round(durationMs / (1000 * 60));
            processingDuration = `${minutes} minutes`;
          }

          return {
            id: ticket.id,
            ticketNumber: ticket.ticket_number,
            vehicleNumber: ticket.vehicle?.vehicle_number || 'Unknown',
            driverName: ticket.driver?.name || 'Not assigned',
            driverContact: ticket.driver?.mobile || '',
            driverLicense: ticket.driver?.license_number || '',
            operationType: ticket.status === 'IN' ? 'check-in' : 'check-out',
            materialType: '', // Not in API response
            auctionLot: '', // Not in API response
            materialQuantity: ticket.net_weight ? `${parseFloat(ticket.net_weight)}` : '',
            checkInTime: ticket.entry_time,
            checkOutTime: ticket.exit_time,
            tareWeight: ticket.tare_weight ? `${parseFloat(ticket.tare_weight)}` : '',
            grossWeight: ticket.gross_weight ? `${parseFloat(ticket.gross_weight)}` : '',
            netWeight: ticket.net_weight ? `${parseFloat(ticket.net_weight)}` : '',
            weighbridgeLocation: '', // Not in API response
            loadingPoint: '', // Not in API response
            destination: '', // Not in API response
            yardLocation: '', // Not in API response
            facility: ticket.delivery_site?.project_name || 'Unknown',
            waybillNumber: '', // Not in API response
            gatePassNumber: '', // Not in API response
            bosNumber: '', // Not in API response
            supervisorName: '', // Not in API response
            safetyStatus: 'approved', // Assuming completed tickets are approved
            technicalClearance: true, // Assuming completed
            financialClearance: true, // Assuming completed
            inspectionNotes: ticket.remarks || '',
            status: status,
            vehiclePhotos: ticket.all_photos,
            processingDuration: processingDuration,
            // Additional API fields
            delivery_site: ticket.delivery_site,
            photos: ticket.photos || []
          };
        });

        console.log('Processed Tickets:', tickets); // Debug log

        this.setState({
          recentTickets: tickets,
          isLoadingTickets: false
        });
      } else {
        toast.error(response.message || 'Failed to fetch weighbridge tickets');
        this.setState({ isLoadingTickets: false });
      }
    } catch (error) {
      console.error('Error fetching weighbridge tickets:', error);
      toast.error('Error loading weighbridge tickets');
      this.setState({ isLoadingTickets: false });
    }
  };

  handleSitesChange = (e) => {
    const siteId = e.target.value;
    const selectedSite = this.state.sites.find(site => site.id == siteId);

    console.log('Selected Site:', selectedSite); // Debug log

    this.setState(prevState => ({
      auctionSite: siteId,
      errors: { ...prevState.errors, auctionSite: '' },
      vehicles: [], // Clear vehicles when site changes
      vehicleId: '', // Clear selected vehicle
      vehicleNumber: '', // Clear vehicle number
      driverName: '', // Clear driver name
      driverContact: '', // Clear driver contact
      driverLicense: '', // Clear driver license

      // Auto-populate load_from and load_to if site has these properties
      loadingPoint: selectedSite?.load_from || '',
      destination: selectedSite?.load_to || '',

      // Also clear material info if needed
      materialType: '',
      auctionLot: selectedSite?.label?.split('.')[1]?.trim() || '', // Extract auction name from label
    }));

    // Fetch vehicles for the selected site
    if (siteId) {
      this.fetchVehiclesList(siteId);

      // Show success message with auto-filled info
      if (selectedSite) {
        toast.success(`Site "${selectedSite.label}" selected. Loading vehicles...`);
      }
    }
  };

  handleVehicleChange = (e) => {
    const vehicleId = e.target.value;
    const selectedVehicle = this.state.vehicles.find(v => v.id == vehicleId);

    console.log('Selected Vehicle Object:', selectedVehicle); // Debug log
    console.log('All Vehicles:', this.state.vehicles); // Debug log

    this.setState({
      vehicleId: vehicleId,
      vehicleNumber: selectedVehicle?.vehicle_number || '',
      vehicleModel: selectedVehicle?.vehicle_model || '',
      driverName: selectedVehicle?.driver?.name || '',
      driverContact: selectedVehicle?.driver?.phone || '',
      driverLicense: selectedVehicle?.driver?.license_number || '',
      errors: { ...this.state.errors, vehicleId: '' }
    });

    // Show success message if vehicle selected
    if (selectedVehicle) {
      toast.success(`Vehicle ${selectedVehicle.vehicle_number} selected`);
    } else {
      toast.error('Vehicle not found in the list');
    }
  };


  render() {
    const {
      ticketId,
      operationType,
      vehicleNumber,
      driverName,
      tareWeight,
      grossWeight,
      netWeight,
      errors,
      searchQuery,
      isEditing,
      loadingPoint,
      destination,
      materialType,
      waybillNumber,
      gatePassNumber,
      bosNumber,
      safetyStatus,
      sites,
      checklist,
      vehiclePhotos,
      auctionSite,
      vehicleId,
      vehicles,
      loadingSites,
      loadingVehicles,
      driverContact,

      supervisorName,
      actualArrival,
      actualDeparture,
      vehiclePhoto,
      weighbridgeLocation,
      type,

    } = this.state;

    const filteredTickets = this.getFilteredTickets();

    return (

      <main className="main-content card material-loading">
        <div className="page-header">
          <button className="button1 button1-primary" onClick={() => window.history.back()}
          ><ArrowBigLeft />  {i18n.t('dashboard')}</button>

          <div className="page-title">
            <h1>{i18n.t('material_loading')}</h1>
          </div>

          <div className="header-actions">
            <button className="button1 button1-secondary" onClick={this.resetForm}>
              New Ticket
            </button>

            <button
              className="button1 button1-primary"
              onClick={operationType === 'check-in' ? this.handleSubmit : this.handleExitSubmit}
              disabled={this.state.isLoading}
            >
              {this.state.isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : operationType === 'check-in' ? (
                'Create Entry Ticket'
              ) : (
                'Record Exit'
              )}
            </button>
          </div>
        </div>

        {/* Quick Stats Bar */}


        <div className="content-grid">

          <div className="right-column">

            <section className="card-t1">

              <div className="form-group full-width section-divider">
                <div className="card-t1-header">
                  <Package size={24} className="header-icon" />
                  <h2>Operation Summary</h2>
                </div>
              </div>

              <div className="operation-summary">
                <div className="summary-item">
                  <span className="label">Mode:</span>
                  <span className={`value ${isEditing ? 'editing' : 'creating'}`}>
                    {isEditing ? 'Editing' : 'Creating New'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Current Operation:</span>
                  <span className={`value ${operationType}`}>
                    {operationType === 'check-in' ? 'Vehicle Check-in' : 'Vehicle Check-out'}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="label">Vehicle:</span>
                  <span className="value">{vehicleNumber || 'Not specified'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Driver:</span>
                  <span className="value">{driverName || 'Not specified'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Material:</span>
                  <span className="value">{materialType || 'Not specified'}</span>
                </div>
                {netWeight && (
                  <div className="summary-item">
                    <span className="label">Net Weight:</span>
                    <span className="value">{netWeight} kg</span>
                  </div>
                )}
              </div>

            </section>

            {/* Recent Tickets */}
            <section className="card-t1">
              <div className="form-group full-width section-divider">
                <div className="card-t1-header">
                  <Clock size={24} className="header-icon" />
                  <h2>
                    Recent Tickets
                    {isEditing && <span className="view-mode">(Click to Load)</span>}
                    <button
                      type="button"
                      className="button1 button1-primary"
                      onClick={this.fetchWeighbridgeTickets}
                      style={{ marginLeft: '10px', fontSize: '12px', padding: '2px 8px' }}
                    >
                      ↻ Refresh
                    </button>
                  </h2>
                </div>

                <div className="form-group" >
                  <select
                    value={auctionSite}
                    onChange={this.handleSitesChange}
                    className={errors.auctionSite ? 'error' : ''}
                    disabled={loadingSites}
                  >
                    <option value="">Select Delivery Site</option>
                    {loadingSites ? (
                      <option value="" disabled>Loading Sites...</option>
                    ) : (
                      sites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.auctionSite && <span className="error-message">{errors.auctionSite}</span>}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.fetchSitesList}
                    style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px', textAlign: "center" }}
                  >
                    ↻ Refresh Sites
                  </button>
                </div>
              </div>

              <div className="form-group">
                <div className="search-box">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search by vehicle, driver, or ticket..."
                    value={searchQuery}
                    onChange={(e) => this.handleSearch(e.target.value)}
                  />
                </div>
              </div>

              {this.state.isLoadingTickets ? (
                <div className="loading-container">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Loading tickets...</span>
                </div>
              ) : (
                <div className="transactions-list-container">
                  <div className="transactions-list">
                    {filteredTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className={`transaction-item ${isEditing && ticket.ticketNumber === ticketId ? 'active' : ''}`}
                        onClick={() => this.handleTicketClick(ticket)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="transaction-header">
                          <span className="vehicle-number">
                            {ticket.vehicleNumber}
                            <strong className="transaction-details">( {ticket.ticketNumber} )</strong>
                          </span>
                          <span className={`status-badge ${ticket.status}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div className="transaction-details">
                          <div>{ticket.delivery_site?.auction_name}</div>
                          <div className="ticket-meta">
                            <small>
                              {ticket.checkInTime ? new Date(ticket.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              {ticket.tareWeight && ` • Tare: ${parseFloat(ticket.tareWeight).toLocaleString()} kg`}
                            </small>
                          </div>
                        </div>
                        <div className="transaction-hover">Click to load this ticket</div>
                      </div>
                    ))}
                    {filteredTickets.length === 0 && (
                      <div className="no-results">No tickets found</div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>

          {/* Left Column - Main Form */}
          <section className="card-t1 vehicle-details">

            <div className="form-grid">

              {/* Vehicle Information Section */}
              <div className="form-group full-width ">
                <div className="section-header section-divider">
                  <div className="card-t1-header">
                    <Truck size={24} className="header-icon" />
                    <h2>
                      {i18n.t('load_information')}
                      {vehicleNumber && ` - (${vehicleNumber})`}
                    </h2>
                  </div>

                  <div style={{ display: "flex", gap: "5px" }}>
                    <button
                      type="button"
                      className="button1 button1-primary"

                    >
                      <Download size={16} />
                      Export As
                    </button>
                    {/* <button
                      type="button"
                      className="no-data-button"

                    >
                      <ScanBarcode size={16} />
                      Scan QR
                    </button> */}
                  </div>

                </div>
              </div>

              {operationType === 'check-in' && (
                <div className="form-group" style={{ width: "220px" }}>
                  <label className="required">{i18n.t('auction_sites')}</label>
                  <select
                    value={auctionSite}
                    onChange={this.handleSitesChange}
                    className={errors.auctionSite ? 'error' : ''}
                    disabled={loadingSites}
                  >
                    <option value="">Select Delivery Site</option>
                    {loadingSites ? (
                      <option value="" disabled>Loading Sites...</option>
                    ) : (
                      sites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.auctionSite && <span className="error-message">{errors.auctionSite}</span>}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.fetchSitesList}
                    style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px', textAlign: "center" }}
                  >
                    ↻ Refresh Sites
                  </button>
                </div>
              )}

              {operationType === 'check-in' && auctionSite && (
                <div className="form-group" style={{ width: "220px" }}>
                  <label>{i18n.t('vehicle_number')}</label>
                  <select
                    value={vehicleId}
                    onChange={this.handleVehicleChange}
                    className="driver-search"
                    disabled={loadingVehicles || vehicles.length === 0}
                  >
                    <option value="">Select Vehicle</option>
                    {loadingVehicles ? (
                      <option value="" disabled>Loading Vehicles...</option>
                    ) : vehicles.length === 0 ? (
                      <option value="" disabled>No vehicles assigned to this site</option>
                    ) : (
                      vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.vehicle_number}
                          {vehicle.vehicle_model ? ` - ${vehicle.vehicle_model}` : ''}
                          {vehicle.driver?.name ? ` (Driver: ${vehicle.driver.name})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={() => auctionSite && this.fetchVehiclesList(auctionSite)}
                    style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px', textAlign: "center" }}
                    disabled={!auctionSite}
                  >
                    ↻ Refresh Vehicles
                  </button>
                </div>
              )}

              {/* Add yard selection if ticket has yards */}
              {operationType === 'check-out' && this.state.currentTicketYards && this.state.currentTicketYards.length > 0 && (
                <div className="form-group">
                  <label className="required">Select Yard</label>
                  <select
                    value={this.state.yardId}
                    onChange={(e) => this.setState({
                      yardId: e.target.value,
                      errors: { ...this.state.errors, yardId: '' }
                    })}
                    className={this.state.errors.yardId ? 'error' : ''}
                  >
                    <option value="">Select a Yard</option>
                    {this.state.currentTicketYards.map(yard => (
                      <option key={yard.id} value={yard.id}>
                        {yard.name} - Loaded: {yard.loaded_weight} kg
                      </option>
                    ))}
                  </select>
                  {this.state.errors.yardId && <span className="error-message">{this.state.errors.yardId}</span>}
                  <small className="text-muted">
                    This delivery site has {this.state.currentTicketYards.length} yard(s)
                  </small>
                </div>
              )}


              {operationType && (
                <div className="form-group">
                  <label className="required">{i18n.t('tare_weight_kg')}</label>
                  <input
                    type="number"
                    placeholder="Empty vehicle weight"
                    value={tareWeight}
                    onChange={(e) => this.handleInputChange('tareWeight', e.target.value)}
                    className={errors.tareWeight ? 'error' : ''}
                    min="0"
                    step="0.01"
                    readOnly={operationType === 'check-out'}

                  />
                  {errors.tareWeight && <span className="error-message">{errors.tareWeight}</span>}
                </div>
              )}

              {operationType === 'check-in' && (
                <>
                  {/* Auto-populated location fields (read-only when site selected) */}
                  <div className="form-group">
                    <label className="required">Load From</label>
                    <input
                      type="text"
                      placeholder="Loading location"
                      value={loadingPoint}
                      onChange={(e) => this.handleInputChange('loadingPoint', e.target.value)}
                      className={auctionSite ? 'readonly-input' : ''}
                      readOnly={!!auctionSite}
                    />
                    {errors.loadingPoint && <span className="error-message">{errors.loadingPoint}</span>}
                  </div>

                  <div className="form-group">
                    <label className="required">Load To</label>
                    <input
                      type="text"
                      placeholder="Destination"
                      value={destination}
                      onChange={(e) => this.handleInputChange('destination', e.target.value)}
                      className={auctionSite ? 'readonly-input' : ''}
                      readOnly={!!auctionSite}
                    />
                    {errors.destination && <span className="error-message">{errors.destination}</span>}
                  </div>
                </>
              )}
              {/* Vehicles dropdown - Show only when site is selected */}
              {/* Vehicles dropdown - Show only when site is selected */}



              {/* Driver Information (auto-populated when vehicle is selected) */}
              {vehicleId && (
                <>
                  <div className="form-group">
                    <label className="required">{i18n.t('driver_name')}</label>
                    <input
                      type="text"
                      placeholder="Driver full name"
                      value={driverName}
                      onChange={(e) => this.handleInputChange('driverName', e.target.value)}
                      className={driverName ? 'readonly-input' : ''}
                      readOnly={!!driverName}
                    />
                    {errors.driverName && <span className="error-message">{errors.driverName}</span>}
                    {!driverName && (
                      <small className="text-muted">No driver assigned. Please enter driver name.</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="required">{i18n.t('driver_number')}</label>
                    <input
                      type="text"
                      placeholder="Driver phone number"
                      value={driverContact}
                      onChange={(e) => this.handleInputChange('driverContact', e.target.value)}
                      className={driverContact ? 'readonly-input' : ''}
                      readOnly={!!driverContact}
                    />
                    {errors.driverContact && <span className="error-message">{errors.driverContact}</span>}
                    {!driverContact && (
                      <small className="text-muted">No driver contact. Please enter phone number.</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>{i18n.t('driver_license')}</label>
                    <input
                      type="text"
                      placeholder="Driver license number"
                      value={this.state.driverLicense}
                      onChange={(e) => this.handleInputChange('driverLicense', e.target.value)}
                      className={this.state.driverLicense ? 'readonly-input' : ''}
                      readOnly={!!this.state.driverLicense}
                    />
                  </div>
                </>
              )}


              {operationType === 'check-out' && (
                <>
                  <div className="form-group">
                    <label className="required">Gross Weight (Kg)</label>
                    <input
                      type="number"
                      placeholder="Loaded vehicle weight"
                      value={grossWeight}
                      onChange={(e) => this.handleInputChange('grossWeight', e.target.value)}
                      className={errors.grossWeight ? 'error' : ''}
                      min="0"
                      step="0.01"
                    />
                    {errors.grossWeight && <span className="error-message">{errors.grossWeight}</span>}
                  </div>

                  <div className="form-group">
                    <label className="required">Net Weight (Kg)</label>
                    <input
                      type="number"
                      placeholder="Calculated net weight"
                      value={
                        tareWeight && grossWeight && parseFloat(tareWeight) > 0 && parseFloat(grossWeight) > 0
                          ? (parseFloat(grossWeight) - parseFloat(tareWeight)).toFixed(2)
                          : ""
                      } readOnly
                      className="readonly-input"
                    />
                    {errors.netWeight && <span className="error-message">{errors.netWeight}</span>}
                  </div>
                </>
              )}



              {/* {(isCheckOut || safetyStatus !== 'pending') && (
                <>
                  <div className="form-group full-width">
                    <label className="required">Checklist</label>
                    <div className="checklist-grid">
                      {Object.entries(checklist).map(([key, value]) => (
                        <div
                          key={key}
                          className={`checklist-item ${value ? 'completed' : ''}`}
                          onClick={() => this.handleChecklistChange(key)}
                        >
                          <div className="checklist-checkbox">
                            {value && <CheckCircle size={16} />}
                          </div>
                          <span className="checklist-label">
                            {i18n.t(key)}
                            {/* {key.split(/(?=[A-Z])/).join(' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())} */}
                          {/* </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}  */}


              {operationType === 'check-out' && (
                <div className="form-group full-width">
                  <label className="required">Vehicle Photos (Required for Check-out)</label>
                  <div className="photo-grid">
                    {vehiclePhotos.map(photo => (
                      <div key={photo.id} className="photo-item">
                        <img src={photo.url} alt={`Vehicle ${vehicleNumber}`} />
                        <button
                          className="remove-photo"
                          onClick={() => this.removePhoto(photo.id)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    <div className="photo-upload">
                      <Upload size={32} />
                      <span>Add Photos (Required for Check-out)</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={this.handlePhotoUpload}
                        className="photo-upload-input"
                      />
                    </div>
                  </div>
                  {errors.photos && <span className="error-message">{errors.photos}</span>}
                  <small className="text-muted">Max 5 photos, each max 5MB. At least one required for check-out.</small>
                </div>
              )}

              {/* Documentation Section - More important for Check-out */}
              {/* {(isCheckOut || waybillNumber || gatePassNumber) && (
                <>
                  <div className="form-group">
                    <label className={isCheckOut ? 'required' : ''}>Waybill Number</label>
                    <input
                      type="text"
                      placeholder="Waybill/document number"
                      value={waybillNumber}
                      onChange={(e) => this.handleInputChange('waybillNumber', e.target.value)}
                      className={errors.waybillNumber ? 'error' : ''}
                    />
                    {errors.waybillNumber && <span className="error-message">{errors.waybillNumber}</span>}
                  </div>

                  <div className="form-group">
                    <label className={isCheckOut ? 'required' : ''}>Gate Pass Number</label>
                    <input
                      type="text"
                      placeholder="Gate pass number"
                      value={gatePassNumber}
                      onChange={(e) => this.handleInputChange('gatePassNumber', e.target.value)}
                      className={errors.gatePassNumber ? 'error' : ''}
                    />
                    {errors.gatePassNumber && <span className="error-message">{errors.gatePassNumber}</span>}
                  </div>

                  <div className="form-group">
                    <label>BOS Number</label>
                    <input
                      type="text"
                      placeholder="BOS document number"
                      value={bosNumber}
                      onChange={(e) => this.handleInputChange('bosNumber', e.target.value)}
                    />
                  </div>
                </>
              )} */}
            </div>
          </section>

          {this.state.showSuccessModal && this.state.createdTicket && (
            <div className="modal-overlay">
              <div className="modal-content success-modal">
                <div className="modal-header">
                  <h3>✓ Ticket Created Successfully!</h3>
                  <button onClick={() => this.setState({ showSuccessModal: false })}>×</button>
                </div>
                <div className="modal-body">
                  <div className="ticket-summary">
                    <div className="ticket-header">
                      <h4>Ticket Details:</h4>
                      <div className="ticket-number">
                        <strong>Ticket #:</strong> {this.state.createdTicket.ticket_number}
                      </div>
                    </div>

                    <div className="ticket-info-grid">
                      <div className="info-item">
                        <span className="label">Vehicle:</span>
                        <span className="value">
                          {this.state.createdTicket.vehicle?.vehicle_number} - {this.state.createdTicket.vehicle?.vehicle_model}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Tare Weight:</span>
                        <span className="value">{this.state.createdTicket.tare_weight} kg</span>
                      </div>

                      <div className="info-item">
                        <span className="label">Entry Time:</span>
                        <span className="value">
                          {new Date(this.state.createdTicket.entry_time).toLocaleString()}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Delivery Site:</span>
                        <span className="value">
                          {this.state.createdTicket.delivery_site?.project_name}
                        </span>
                      </div>

                      <div className="info-item">
                        <span className="label">Status:</span>
                        <span className="value status-badge active">{this.state.createdTicket.status}</span>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button
                        className="button1 button1-primary"
                        onClick={() => {
                          console.log('Print ticket:', this.state.createdTicket);
                        }}
                      >
                        Print Ticket
                      </button>
                      <button
                        className="button1 button1-secondary"
                        onClick={() => this.setState({ showSuccessModal: false })}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>


    );
  }
}

export default MaterialLoading;